import React, { useEffect } from 'react';
import { GENDERS } from '@/constants/professionalTags';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	User,
	CalendarDays,
	Lock,
	ArrowRightCircle,
	ShieldCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Input, InputWrapperAuth } from '@/components/ui';
import { maskDate, normalizeName, transformBirthday } from '@/utils';
import { getStepData, saveStepData } from '@/utils/localStorageUtils';
import { useModal } from '@/hooks/useModal';
import ProfessionalTermsOfUsePage from '@/pages/professional-terms-of-use';
import { Header } from '@/pages/public/landing/components/Header';

export const STORAGE_KEYS = {
	PROFESSIONAL_AUTH: 'p_auth',
	STEP_PERSONAL: 's_p',
	STEP_DOCUMENTATION: 's_d',
	STEP_CONTACT: 's_c',
	STEP_MODALITY: 's_m',
};

const schema = z.object({
	gender: z
		.enum(['FEMININO', 'MASCULINO', 'PREFIRO_NAO_INFORMAR'])
		.optional(),
	name_prefix: z
		.string()
		.regex(/^[A-Za-zÀ-ÖØ-öø-ÿ.\s]*$/, 'Apenas letras')
		.transform(normalizeName)
		.optional()
		.or(z.literal('')),
	first_name: z
		.string()
		.regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'Apenas letras')
		.transform(normalizeName)
		.refine((val: string) => val.length >= 3, 'Mínimo 3 caracteres'),
	last_name: z
		.string()
		.regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/, 'Apenas letras')
		.transform(normalizeName)
		.refine((val: string) => val.length >= 3, 'Mínimo 3 caracteres'),
	birthday: z
		.string()
		.regex(
			/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19\d{2}|20\d{2})$/,
			'Data inválida',
		)
		.transform(transformBirthday),
	password: z
		.string()
		.min(6, 'Mínimo 6 caracteres')
		.regex(/^(?=.*[A-Za-z])(?=.*\d).+$/, 'Deve conter letra e número'),
	terms_accepted: z.literal(true, {
		errorMap: () => ({ message: 'Você deve aceitar os termos' }),
	}),
});

type FormData = z.infer<typeof schema>;

// Componente de Label padronizado e interativo
const FormLabel = ({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) => (
	<label
		htmlFor={htmlFor}
		className="mb-1 ml-1 block cursor-pointer text-[10px] font-black uppercase tracking-widest text-stone-500 transition-colors hover:text-stone-700"
	>
		{children}
	</label>
);

const StepOne = () => {
	const navigate = useNavigate();
	const { openModal } = useModal();

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const onSubmit = (data: FormData) => {
		saveStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_PERSONAL,
			data,
		);
		navigate('/profissional/cadastro/info-documentacao');
	};

	useEffect(() => {
		const savedData = getStepData(
			STORAGE_KEYS.PROFESSIONAL_AUTH,
			STORAGE_KEYS.STEP_PERSONAL,
		);
		if (savedData) {
			Object.entries(savedData).forEach(([key, value]) => {
				if (key === 'birthday' && typeof value === 'string') {
					try {
						const datePart = value.split('T')[0];
						if (datePart.includes('-')) {
							const [year, month, day] = datePart.split('-');
							setValue('birthday', `${day}/${month}/${year}`);
							return;
						}
					} catch (e) {
						// Fallback
					}
				}
				setValue(key as keyof FormData, value as any);
			});
		}
	}, [setValue]);

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Informações Pessoais | Plumaa</title>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					{/* TÍTULOS */}
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							Dados Pessoais
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Etapa 1 de 4: Vamos começar pelo básico
						</p>
					</div>

					{/* FORMULÁRIO */}
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						<div className="space-y-4">
							{/* NOME */}
							<div>
								<FormLabel htmlFor="field-firstname">
									NOME
								</FormLabel>
								<InputWrapperAuth
									className="max-w-none"
									icon={<User className={iconCommonClass} />}
									error={errors.first_name?.message}
									value={watch('first_name')}
									displayError
								>
									<Input
										{...register('first_name')}
										id="field-firstname"
										value={watch('first_name') || ''}
										onChange={(e) =>
											setValue(
												'first_name',
												e.target.value.replace(
													/\s+/g,
													' ',
												),
												{ shouldValidate: false },
											)
										}
										placeholder="Digite seu nome"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							{/* SOBRENOME */}
							<div>
								<FormLabel htmlFor="field-lastname">
									SOBRENOME
								</FormLabel>
								<InputWrapperAuth
									className="max-w-none"
									icon={<User className={iconCommonClass} />}
									error={errors.last_name?.message}
									value={watch('last_name')}
									displayError
								>
									<Input
										{...register('last_name')}
										id="field-lastname"
										value={watch('last_name') || ''}
										onChange={(e) =>
											setValue(
												'last_name',
												e.target.value.replace(
													/\s+/g,
													' ',
												),
												{ shouldValidate: false },
											)
										}
										placeholder="Digite seu sobrenome"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							{/* PREFIXO */}
							<div>
								<FormLabel htmlFor="field-prefix">
									Título Profissional (opcional)
								</FormLabel>
								<InputWrapperAuth
									className="max-w-none"
									icon={<User className={iconCommonClass} />}
									error={errors.name_prefix?.message}
									value={watch('name_prefix')}
									displayError
								>
									<Input
										{...register('name_prefix')}
										id="field-prefix"
										value={watch('name_prefix') || ''}
										onChange={(e) =>
											setValue(
												'name_prefix',
												e.target.value.replace(
													/\s+/g,
													' ',
												),
												{ shouldValidate: false },
											)
										}
										placeholder="Dr., Dra., etc."
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							{/* DATA NASCIMENTO */}
							<div>
								<FormLabel htmlFor="field-gender">
									Gênero (opcional)
								</FormLabel>
								<select
									id="field-gender"
									{...register('gender')}
									defaultValue="PREFIRO_NAO_INFORMAR"
									className="h-10 w-full rounded-lg border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-stone-400 focus:outline-none"
								>
									{GENDERS.map((g) => (
										<option key={g.key} value={g.key}>
											{g.label}
										</option>
									))}
								</select>
							</div>

							<div>
								<FormLabel htmlFor="field-birthday">
									NASCIMENTO
								</FormLabel>
								<InputWrapperAuth
									className="max-w-none"
									icon={
										<CalendarDays
											className={iconCommonClass}
										/>
									}
									error={errors.birthday?.message}
									value={watch('birthday')}
									displayError
								>
									<Input
										{...register('birthday')}
										id="field-birthday"
										onChange={(e) =>
											setValue(
												'birthday',
												maskDate(e.target.value),
												{
													shouldValidate: true,
												},
											)
										}
										value={maskDate(
											watch('birthday') || '',
										)}
										placeholder="dd/mm/aaaa"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
										maxLength={10}
									/>
								</InputWrapperAuth>
							</div>

							{/* SENHA */}
							<div>
								<div className="mb-1 flex items-center justify-between px-1">
									<FormLabel htmlFor="field-password">
										SENHA
									</FormLabel>
									<span className="text-[9px] font-bold uppercase italic text-stone-500">
										mín. 6 chars
									</span>
								</div>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.password?.message}
									value={watch('password')}
									displayError
								>
									<Input
										type="password"
										id="field-password"
										{...register('password')}
										placeholder="********"
										className="h-9 w-full border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>
						</div>

						{/* TERMOS DE USO */}
						<div className="flex items-center gap-2 pt-2">
							<input
								type="checkbox"
								id="terms"
								{...register('terms_accepted')}
								className="h-4 w-4 cursor-pointer accent-stone-900"
							/>
							<label
								htmlFor="terms"
								className="cursor-pointer select-none text-[11px] leading-tight text-stone-500"
							>
								Confirmo que li e aceito os{' '}
								<button
									type="button"
									onClick={(e) => {
										e.preventDefault();
										openModal({
											title: 'Termos',
											content: (
												<ProfessionalTermsOfUsePage />
											),
										});
									}}
									className="font-black text-stone-900 outline-none hover:underline"
								>
									termos de uso
								</button>
							</label>
						</div>

						<div className="space-y-4 pt-2">
							<Button
								type="submit"
								className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800"
								text="Próxima etapa"
								endIcon={
									<ArrowRightCircle
										size={14}
										className="ml-1"
									/>
								}
							/>

							{/* FOOTER */}
							<div className="text-center">
								<button
									type="button"
									onClick={() =>
										navigate('/profissional/cadastro')
									}
									className="mx-auto flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-widest text-stone-400 transition-colors hover:text-stone-900"
								>
									Voltar para o início
								</button>
							</div>
						</div>

						<div className="mt-2 flex items-center justify-center gap-2 border-t border-stone-50 pt-4 text-[9px] font-black uppercase tracking-widest text-stone-300">
							<ShieldCheck size={14} />
							<span>Seus dados estão protegidos pela Plumaa</span>
						</div>
					</form>
				</div>
			</main>
		</div>
	);
};

export { StepOne };
